import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { Form, message, Modal, Row } from "antd";
import moment from "moment";

//metodos
import { getConceptosFromFacturas, getFactura, getNewFolio, timbradoIndividual } from "./services";

//sections
import DocumentoFiscalSection from "./sections/DocumentoFiscalSection";
import RelacionFacturasSection from "./sections/RelacionFacturasSection";
import EmisorSection from "./sections/EmisorSection";
import ReceptorSection from "./sections/ReceptorSection";
import PagosProductosSection from "./sections/PagosProductosSection";
import ConceptosProductosSection from "./sections/ConceptosProductosSection";
import TotalesSection from "./sections/TotalesSection";

// Valores comunes a todos
const COMMON_DEFAULT_VALUES = {
    Fecha: moment(),
    Exportacion: "01",
    LugarExpedicion: "",
    Moneda: "MXN",
    TipoCambio: 1,
    FormaPago: "01",
    enviar_receptor: true,
    enviar_responsable: true,
    Emisor: {
        Nombre: "",
        Rfc: "",
        RegimenFiscal: ""
    },
    Receptor: {
        Nombre: "",
        Rfc: "",
        UsoCFDI: "",
        DomicilioFiscalReceptor: "",
        RegimenFiscalReceptor: ""
    },
    Conceptos: [],
    SubTotal: 0,
    Descuento: 0,
    Impuestos: {
        TotalImpuestosTrasladados: 0
    },
    Total: 0,
    Observaciones: ""
};

// Valores específicos por tipo
const DEFAULT_VALUES = {
    NC: {
        ...COMMON_DEFAULT_VALUES,
        Serie: process.env.REACT_APP_NOTA_CREDITO,
        TipoDeComprobante: "E",
        MetodoPago: "PUE",
        descuento: null,
        DescuentoPorcentual: 0,
    },
};

/**
 * @description Este componente es el formulario que se muestra dentro del drawer de facturación,
 * aquí se capturan los datos necesarios para el timbrado de la factura.
 * @param {*} props
 * @param {*} ref - Referencia al formulario
 * @returns
 */
const FacturaTimbradoForm = forwardRef(({ 
    tipoFactura = "NC",
    disabled = false,
    cliente = null,
    clienteId = null,
    onCloseEvent = () => {}
}, ref) => {

    const [ FacturasList, setFacturasList ] = React.useState([]) //Lista donde se almacenan las facturas para relacionar

    useEffect(() => {
        getFolio();
    }, []);

    const getFolio = async () => {
        const folio = await getNewFolio(tipoFactura);
        if (folio && ref?.current) {
            ref.current.setFieldsValue({ Folio: folio });
        }
    };

    /**
     * @description Maneja el submit del formulario de timbrado
     * @param {*} values - Valores del formulario
     */
    const handleFinish = async (values) => {
        try {

            let dataToSubmit = {
                ...values, //valores del formulario
                cliente_id: clienteId,
            }

            let response = await timbradoIndividual(dataToSubmit)
            message.success("Factura timbrada exitosamente");
            onCloseEvent(true)
        } catch (error) {
            console.error("Error en el timbrado individual:", error.response);
            if (Array.isArray(error.response?.data?.data)) {
                Modal.error({
                    title: 'Error al timbrar la factura',
                    content: <ul>
                        {error.response.data.data.map((e, index) => (
                            <li key={index}>
                                {typeof e === 'object' ? JSON.stringify(e) : e}
                            </li>
                        ))}
                    </ul>
                })
            } else {
                message.error('Error al timbrar la factura. Por favor, intente nuevamente.')
            }
        }

    };

    /**
     * @description Maneja los errores de validación del formulario
     * @param {*} errorInfo - Información de los errores
     */
    const handleFinishFailed = (errorInfo) => {
        console.error("Errores en el formulario:", errorInfo);
    };

    const handleOnSelectFactura = async (factura_id) => {
        try {
            let factura = await getFactura(factura_id);
            if(!factura) throw new Error("No se pudo obtener la factura seleccionada");

            let data = getConceptosFromFacturas(factura, FacturasList);

            setFacturasList((prev) => [...prev, factura]);

            
            if (ref?.current) {
                ref.current.setFieldsValue({
                    Conceptos: data.conceptos_facturas,
                    SubTotal: data.monto,
                    Total: data.total,
                    Impuestos: {
                        TotalImpuestosTrasladados: data.imp_trasladados
                    }
                });
            }
            
        } catch (error) {
            console.error("Error al obtener la factura relacionada:", error);
            message.error("Error al obtener la factura relacionada. Por favor, intente nuevamente.");
        }
    }


    const handleOnDeselectFactura = (factura_id) => {
        setFacturasList((prev) => prev.filter(f => f._id !== factura_id))
        let data = getConceptosFromFacturas(null, FacturasList.filter(f => f._id !== factura_id))
        if (ref?.current) {
            ref.current.setFieldsValue({
                Conceptos: data.conceptos_facturas,
                SubTotal: data.monto,
                Total: data.total,
                Impuestos: {
                    TotalImpuestosTrasladados: data.imp_trasladados
                }
            });
        }
    }
    return (
        <Form
            ref={ref}
            name="timbrado-factura"
            layout="vertical"
            initialValues={DEFAULT_VALUES[tipoFactura]}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            scrollToFirstError={true}
        >
            <Row justify="center" align="center">
                <img
                    src="/images/sat.svg"
                    alt="SAT - Servicio de Administración Tributaria"
                    width={"100%"}
                    style={{
                        marginTop: "2em",
                        marginBottom: "4em",
                        maxWidth: 350,
                    }}
                />
            </Row>

            <DocumentoFiscalSection />

            {tipoFactura === "NC" && <RelacionFacturasSection 
                clienteId={cliente?._id} 
                onSelectFactura={handleOnSelectFactura}
                onDeselectFactura={handleOnDeselectFactura}
            />}

            <EmisorSection/>

            <ReceptorSection cliente={cliente} />

            <PagosProductosSection />

            <ConceptosProductosSection />

            <TotalesSection tipoFactura={tipoFactura}/>
        </Form>
    );
});

export default FacturaTimbradoForm;
